/**
 * @copyright Copyright 2025 Epic Systems Corporation
 * @file Connectors Organization Management Row component
 * @module Epic.AppOrchard.OrganizationManagement.ConnectorsOrganization
 */

import { EditIcon } from "@chakra-ui/icons";
import { HStack, IconButton, Tag, TagLabel, Td, Text, Tr, Wrap, WrapItem } from "@chakra-ui/react";
import { ConnectorsAppTag } from "ao/components/ConnectorManagement/frameworkComponents/Shared/ConnectorsAppTag";
import { AOLink } from "ao/components/Core";
import { useIsOverflowing } from "ao/hooks/useIsOverflowing";
import { useSiteInfoState } from "ao/state/siteInfo";
import { IColumnDefinition, ISiteInfo, IUserSecurity } from "ao/types";
import { IConnectorsOrganizationListItem } from "ao/types/organizationManagement";
import { FC, memo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	getConnectorOrganizationEditRelativeUrl,
	getConnectorOrganizationEditUrl,
} from "routes/connectorOrganizationEdit";
import { getConnectorsOrganizationText } from "../../data/columns";
import { OrgListConnectorsColumnKey } from "../../data/constants";

interface IRowProps {
	userSecurity: IUserSecurity;
	siteInfo: ISiteInfo;
	organization: IConnectorsOrganizationListItem;
	columns: IColumnDefinition[];
}

export const OrganizationRow: FC<IRowProps> = memo((props) => {
	const { organization, columns, userSecurity } = props;
	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const isConnectorsAdmin = userSecurity.canManageConnectorsAppsOrgs && siteInfo.isAdminSite;

	const location = useLocation();
	const navigate = useNavigate();

	const appContainerRef = useRef<HTMLDivElement>(null);
	const appContentRef = useRef<HTMLDivElement>(null);

	const isAppOverflowing = useIsOverflowing(appContainerRef, appContentRef, 12);

	return (
		<Tr display="table-row" color="inherit">
			{columns.map((column) =>
				column.key === OrgListConnectorsColumnKey.Id ? (
					<Td
						key={column.key}
						title={
							isConnectorsAdmin
								? "Go to organization edit page"
								: getConnectorsOrganizationText(
										organization,
										column.key as OrgListConnectorsColumnKey,
								  )
						}
						w={column.width}
						minWidth={column.minWidth}
						maxWidth={column.maxWidth}
					>
						{isConnectorsAdmin ? (
							<AOLink url={getConnectorOrganizationEditRelativeUrl(organization.id)}>
								{getConnectorsOrganizationText(
									organization,
									column.key as OrgListConnectorsColumnKey,
								)}
							</AOLink>
						) : (
							<Text>
								{getConnectorsOrganizationText(
									organization,
									column.key as OrgListConnectorsColumnKey,
								)}
							</Text>
						)}
					</Td>
				) : column.key === OrgListConnectorsColumnKey.ActionLinks ? (
					<Td key={column.key} w={column.width} minW={column.minWidth} maxW={column.maxWidth}>
						<HStack>
							<IconButton
								icon={<EditIcon />}
								aria-label="Edit Organization"
								title="Edit Organization"
								onClick={() =>
									navigate(
										{
											pathname: getConnectorOrganizationEditUrl(organization.id),
										},
										{ state: { from: location.pathname + location.search } },
									)
								}
							/>
						</HStack>
					</Td>
				) : column.key === OrgListConnectorsColumnKey.Apps ? (
					<Td key={column.key} w={column.width} minW={column.minWidth} maxW={column.maxWidth}>
						{organization.apps?.length ? (
							<>
								<div
									ref={appContainerRef}
									style={{
										maxHeight: "87px",
										overflow: "hidden",
										display: "block",
										paddingBottom: "4px",
									}}
								>
									<Wrap ref={appContentRef} width="100%">
										{organization.apps.map((appName, index) => (
											<WrapItem key={index} maxW="100%">
												<Tag
													variant="subtle"
													colorScheme="blue"
													paddingX={2}
													paddingY={1}
													w="100%"
													title={appName}
												>
													<TagLabel lineHeight="1.4">{appName}</TagLabel>
												</Tag>
											</WrapItem>
										))}
									</Wrap>
								</div>
								{isAppOverflowing && (
									<Text
										fontSize="xs"
										color="gray.500"
										mt={1}
										title={organization.apps.join(", ")}
										textDecoration="underline"
									>
										More apps not shown
									</Text>
								)}
							</>
						) : null}
					</Td>
				) : (
					<Td
						key={column.key}
						title={getConnectorsOrganizationText(
							organization,
							column.key as OrgListConnectorsColumnKey,
						)}
						w={column.width}
						minW={column.minWidth}
						maxW={column.maxWidth}
					>
						<Text
							noOfLines={3}
							whiteSpace="pre-wrap"
							overflow="hidden"
							textOverflow="ellipsis"
							lineHeight="1.5"
						>
							{getConnectorsOrganizationText(
								organization,
								column.key as OrgListConnectorsColumnKey,
							)}
						</Text>
						{column.key === OrgListConnectorsColumnKey.Name && organization.isEpicOnFhir && (
							<ConnectorsAppTag
								label="Epic On FHIR"
								title="This organization was created in Epic on FHIR. Details may not be reliable."
								colorScheme="orange"
								variant="subtle"
								marginTop="5px"
							/>
						)}
					</Td>
				),
			)}
		</Tr>
	);
});
